/* eslint-disable max-lines */
import store from '@/store'
import { computed, onUnmounted, ref, toRef, watch } from 'vue'
import KisweDate from '@/modules/common/classes/KisweDate'
import { useTranscodingRegions } from '@/modules/transcodingregions/compositions'
import { useNetworkStats } from '@/modules/status/compositions'
import { useTeamBranding } from '@/modules/teams/compositions'
import VideoObject, { VideoObjectConfig } from '@/classes/VideoObject'
import { SelfTestState } from '@/modules/selftest/types'
import { ConnectStatus } from '@/types/usermedia'
import { SelfTestStats } from '@/types/users'
import type { NetworkStats, UserNetworkStats } from '@/types/networkstats'
import {
  getVideoConfig, checkPendingTime, SelfTestQuestions, SelfTestQuestionInfo
} from '@/modules/selftest/utils/selftest'
import { generateRandomString } from '@/modules/common/utils'
import { Feature, hasFeature } from '@/featureFlags/featureFlags'
import JanusVideoObject, { JanusVideoObjectConfig } from '@/classes/JanusVideoObject'
import { useAddLog } from '@/modules/logging/compositions'
import env from '@/env'
import fb from '@/firebase'
import { useDeviceChangeDetection } from '@/modules/usermedia/compositions'

const openSelfTest = async (region: string) => {
  const startSelfTestFn = fb.functions.httpsCallable('startSelfTest')
  try {
    await startSelfTestFn({ regionName: region })
  } catch (error) {
    console.log(error)
  }
}

const SELFTEST_DURATION = 60
const mediaStatusChecks: (keyof SelfTestStats)[] = [
  'seevideo', 'seepreview', 'hearaudio', 'hearpreview', 'camerapermission', 'microphonepermission']

const useUdp = computed(() => store.getters.user.useUdp)
const permissions = computed(() => store.state.usermedia.permissions)
const isAnonymous = computed(() => !!store.state.user.profile?.anonymous)
const userProfile = computed(() => store.state.user.profile)
const isJanus = computed(() => hasFeature(Feature.JANUS) ?? false)
const selectedDevices = computed(() => {
  return {
    audio: userProfile.value?.selectedAudioDevice,
    video: userProfile.value?.selectedVideoDevice
  }
})

const useSelfTest = (inCast?: boolean) => {
  const selftestStream = toRef(useTeamBranding().branding.value, 'selftestStream')
  const { stats } = useNetworkStats()
  const { myRegion } = useTranscodingRegions()
  const { resetHasDetectedDeviceChanges } = useDeviceChangeDetection()
  const { addLogNoWait } = useAddLog()
  const wrtcServer = computed(() => {
    if (env.chinaBuild) return store.state.usermedia.wrtcServerChinaProxy
    return store.state.usermedia.wrtcServer
  })
  const connectStatus = computed(() => store.state.usermedia.connectStatus)
  const currentStep = ref(0)
  const selfTestState = ref(SelfTestState.IDLE)
  const selfTestResult = ref(new SelfTestStats())
  const selfTestQuestions = ref(new Map<number, SelfTestQuestionInfo>(SelfTestQuestions))

  const pendingTimer = ref<ReturnType<typeof setInterval>|null>(null)
  const checkErrorStatus = (state: SelfTestState) => {
    if (pendingTimer.value) clearInterval(pendingTimer.value)
    switch (state) {
      case SelfTestState.CASTING:
        pendingTimer.value = checkPendingTime(SELFTEST_DURATION,
          () => changeSelfTestState(SelfTestState.NO_REPLY))
        break
      case SelfTestState.NO_REPLY:
        pendingTimer.value = checkPendingTime(10,
          () => changeSelfTestState(SelfTestState.FAIL))
        break
      default:
        break
    }
  }

  const changeSelfTestState = (state: SelfTestState) => {
    checkErrorStatus(state)
    selfTestState.value = state
  }

  const startSelfTest = async () => {
    await resetHasDetectedDeviceChanges()
    changeSelfTestState(SelfTestState.CASTING)
    await store.dispatch.usermedia.setOptimalWrtcServer()
    try {
      const videoId = generateRandomString(6)
      if (isJanus.value) {
        createRemoteJanusVideoObject(`https://${ wrtcServer.value }/whep/endpoint/${ selftestStream.value }`, videoId)
      } else {
        createRemoteVideoObject(`rtsp://${ wrtcServer.value }:1935/livetcp/${ selftestStream.value }`, videoId)
      }
      selfTestResult.value.camerapermission = permissions.value.video ?? false
      selfTestResult.value.microphonepermission = permissions.value.audio ?? false
      if (isAnonymous.value) changeSelfTestState(SelfTestState.REPLY)
    } catch (error) {
      console.error(error)
      changeSelfTestState(SelfTestState.FAIL)
    }
  }

  const restartSelfTest = () => {
    currentStep.value = 0
    selfTestResult.value = new SelfTestStats()
    changeSelfTestState(SelfTestState.PREVIEW)
  }

  const answerQuestion = async (answer: boolean) => {
    const current = selfTestQuestions.value.get(currentStep.value)
    if (!current) return
    selfTestResult.value[current.type] = answer
    const isLastQuestion = selfTestQuestions.value.size === currentStep.value + 1
    if (isLastQuestion) {
      requestNetworkCheck()
      await updateSelfTestResult()
    } else {
      currentStep.value++
    }
  }

  const checkValidNetworkStats = () => {
    const isHighEncoderCpuUsage = (stats.value?.encoder?.cpu_usage ?? 0) > 80
    const hasLowUploadScore = (stats.value?.upload?.score ?? 0) < 2
    if (isHighEncoderCpuUsage || hasLowUploadScore) return false
    return true
  }

  const requestNetworkCheck = () => {
    changeSelfTestState(SelfTestState.CHECKING_NETWORK)
    const isValidMediaStatus = mediaStatusChecks.every((check) => selfTestResult.value[check])
    // NOTE: skip network check until we figure out what the actual problem
    const isValidNetworkStats = isAnonymous.value || checkValidNetworkStats()
    if (!isValidNetworkStats || !isValidMediaStatus) changeSelfTestState(SelfTestState.FAIL)
    else changeSelfTestState(SelfTestState.SUCCESS)
  }

  const remoteVideoObject = ref<VideoObject|JanusVideoObject|null>(null)
  const createRemoteJanusVideoObject = (streamUrl: string, videoId: string) => {
    const config = getVideoConfig({ streamUrl, videoId,
      udpEnabled: useUdp.value, chinaSupport: myRegion.value?.chinaProxy }, true)
    config.callback = async (videoObject: JanusVideoObject) => { remoteVideoObject.value = videoObject }
    config.killStream = async () => { remoteVideoObject.value = null }
    config.restartStream = () => {
      addLogNoWait(`Stream restarted for ${streamUrl}`, 'info')
    }
    const videoObject = new JanusVideoObject(config as JanusVideoObjectConfig)
    videoObject.onNetworkStats(onRemoteNetworkStats)
  }

  const createRemoteVideoObject = (streamUrl: string, videoId: string) => {
    const config = getVideoConfig({ streamUrl, videoId,
      udpEnabled: useUdp.value, chinaSupport: myRegion.value?.chinaProxy }, false)
    config.callback = async (videoObject: VideoObject) => { remoteVideoObject.value = videoObject }
    config.killStream = async (videoObject: VideoObject) => { killRemoteStream(videoObject) }
    config.restartStream = () => { createRemoteVideoObject(streamUrl, videoId) }
    const videoObject = new VideoObject(config as VideoObjectConfig)
    videoObject.onNetworkStats(onRemoteNetworkStats)
  }

  const onRemoteNetworkStats = (remoteStats: NetworkStats) => {
    selfTestResult.value.network.download = {
      bandwidth: stats.value?.download?.bandwidth ?? 0,
      jitter: remoteStats.jitter ?? 0,
      latency: remoteStats.latency ?? 0,
      score: remoteStats.score ?? 0
    }
  }

  const updateSelfTestResult = async () => {
    selfTestResult.value.timestamp = KisweDate.now()
    selfTestResult.value.region = wrtcServer.value ?? 'wrtc-va1.kiswe.com'
    if (stats.value?.upload) selfTestResult.value.network.upload = stats.value.upload
    const result = await store.dispatch.user.updateCurrentProfile((profile) =>
      profile.setSelfTestStats(selfTestResult.value))
    result.logIfError('Could not update self test stats')
  }

  const killRemoteStream = (videoObject: VideoObject|JanusVideoObject) => {
    videoObject.stopPlay()
    remoteVideoObject.value = null
  }

  const killAllStreams = async (remoteVideoObject: VideoObject|JanusVideoObject) => {
    try {
      killRemoteStream(remoteVideoObject)
      await store.dispatch.usermedia.stopCasting()
    } catch (error) { console.log(error) }
  }

  watch([stats, connectStatus, selfTestState], (newValues) => {
    if (isAnonymous.value) return
    const [newStats, newConnectStatus, newSelfTestState] = newValues as [UserNetworkStats, ConnectStatus, SelfTestState]
    if (newStats?.upload?.score
      && newConnectStatus === ConnectStatus.Connected
      && newSelfTestState === SelfTestState.CASTING
    ) {
      changeSelfTestState(SelfTestState.REPLY)
    }
  })

  watch(selectedDevices, async (newVal) => {
    if (selfTestState.value !== SelfTestState.IDLE && selfTestState.value !== SelfTestState.PREVIEW) return
    if ([ConnectStatus.Connecting, ConnectStatus.Connected].includes(connectStatus.value)) return
    await store.dispatch.usermedia.initLocalMediaStreamMinimalConstraints()
    if (selfTestState.value === SelfTestState.IDLE && newVal.audio && newVal.video) {
      changeSelfTestState(SelfTestState.PREVIEW)
    }
  }, { immediate: true, deep: true })

  onUnmounted(async () => {
    if (pendingTimer.value) clearInterval(pendingTimer.value)
    if (remoteVideoObject.value) {
      // NOTE: remoteVideoObject.value type is defined VideoObject.
      // but private methods in VideoObject class are not defined in VideoObject type.
      // and it occurs TS complaint so use assertion to prevent it.
      await killAllStreams(remoteVideoObject.value as VideoObject)
    } else if (!inCast) {
      await store.dispatch.usermedia.stopCasting()
    }
    changeSelfTestState(SelfTestState.IDLE)
  })
  return {
    answerQuestion,
    currentQuestion: computed(() => selfTestQuestions.value.get(currentStep.value)?.questionKey),
    currentQuestionStream: computed(() => selfTestQuestions.value.get(currentStep.value)?.streamType),
    killAllStreams,
    killRemoteStream,
    onRemoteNetworkStats,
    openSelfTest,
    remoteVideoObject,
    restartSelfTest, startSelfTest,
    selfTestResult, selfTestState
  }
}

export default useSelfTest
